import { notification, NotificationConfig, NotificationType } from '@routific/hive';
import { TFunction } from 'i18next';
import { UNKNOWN_STOP } from './scheduling/constants';
import i18n from '../i18n';
import { DateTime } from 'luxon';
import { defaultDisplayDateFormat } from '../constants';

const CREATE_ORDER_SUCCESS_TOAST_KEY = 'order-created';
const CREATE_ORDER_FAILURE_TOAST_KEY = 'order-created-failed';
const DELETED_STOP_TOAST_KEY = 'delete-stop-success';
const DELETED_STOP_FAILED_TOAST_KEY = 'delete-stop-failed';
const UPDATED_STOP_FAILED_TOAST_KEY = 'update-stop-failure';
const ORDER_ADDED_TOAST_KEY = 'order-added';
const ORDER_UPDATED_TOAST_KEY = 'order-updated';
const ORDER_UNSCHEDULED_TOAST_KEY = 'order-unscheduled';

export const createOrderSuccessNotification = (orderId: string, orderName?: string | null) => ({
  id: `${CREATE_ORDER_SUCCESS_TOAST_KEY}-${orderId}`,
  type: NotificationType.SUCCESS,
  message: orderName ? `Added new stop for ${orderName}` : 'Added new stop',
  priority: true,
  dataCy: 'geocode-stop-success-notification',
});

export const deletedVisitSuccessNotification = (visitId: string, visitName?: string | null) => ({
  id: `${DELETED_STOP_TOAST_KEY}-${visitId}`,
  type: NotificationType.SUCCESS,
  message: `Deleted ${visitName || UNKNOWN_STOP}`,
  priority: true,
  dataCy: 'delete-stop-success-notification',
});

export const deletedVisitFailedNotification = (t: TFunction, visitId: string, visitName?: string | null) => ({
  id: `${DELETED_STOP_FAILED_TOAST_KEY}-${visitId}`,
  type: NotificationType.ERROR,
  message: t('deleteVisit.failure.message', { visitName: visitName || UNKNOWN_STOP }),
  priority: true,
  dataCy: 'delete-stop-failed-notification',
});

export const createOrderFailureNotification = (orderName?: string | null) => ({
  // Using timestamp as unique key, because the failed visit may not have an ID from the backend
  id: `${CREATE_ORDER_FAILURE_TOAST_KEY}-${Date.now()}`,
  type: NotificationType.ERROR,
  message: `Couldn't add new stop for ${orderName || UNKNOWN_STOP}. Please try again in a few minutes.`,
  priority: true,
  dataCy: 'geocode-stop-failure-notification',
});

export const notifySheetUploadFailure = (error: { name: string; message: string }) => {
  const { message } = error;

  notification({
    type: NotificationType.ERROR,
    id: 'sheet-upload-failure',
    message,
    dataCy: 'sheet-upload-failure-notification',
  });
};

export const notifyImportVisitsSuccess = (count: number) => {
  notification({
    type: NotificationType.SUCCESS,
    id: 'import-visits-success',
    message: `Uploaded ${count} stops.`,
    dataCy: 'import-visits-success-notification',
  });
};

export const notifyImportVisitsFailure = () => {
  notification({
    type: NotificationType.ERROR,
    id: 'import-visits-failure',
    message: 'Failed to save stops',
    dataCy: 'import-stops-failure-notification',
  });
};

export const updateStopFailureNotification = (visitId: string, name?: string | null, onButtonClick?: () => void): NotificationConfig => ({
  type: NotificationType.ERROR,
  id: `${UPDATED_STOP_FAILED_TOAST_KEY}-${visitId}`,
  message: `Could not save changes to ${name || UNKNOWN_STOP}`,
  dataCy: 'update-stop-failure-notification',
  linkMessage: onButtonClick ? 'View Stop' : undefined,
  linkOnClick: onButtonClick,
});


export const notifyOrderAddedSuccess = (count: number, date: string, onButtonClick?: () => void) => ({
  id: `${ORDER_ADDED_TOAST_KEY}`,
  type: NotificationType.SUCCESS,
  message: i18n.t('notifications:addVisit.create_success', { count }),
  priority: true,
  dataCy: 'order-added-success-notification',
  linkMessage: onButtonClick ? i18n.t('notifications:addVisit.create_success_date_link', { count, date: DateTime.fromISO(date).toFormat(defaultDisplayDateFormat) }) : undefined,
  linkOnClick: onButtonClick,
});

export const notifyOrderUpdatedSuccess = (count: number, date: string, onButtonClick?: () => void) => {
  return {
    id: `${ORDER_UPDATED_TOAST_KEY}`,
    type: NotificationType.SUCCESS,
    message: i18n.t('notifications:editVisit.update_success', { count }),
    priority: true,
    dataCy: 'order-updated-success-notification',
    linkMessage: onButtonClick ? i18n.t('notifications:editVisit.update_success_date_link', { count, date: DateTime.fromISO(date).toFormat(defaultDisplayDateFormat) }) : undefined,
    linkOnClick: onButtonClick,
  };
};

export const notifyOrderUnscheduledSuccess = (count: number, date: string, onButtonClick?: () => void) => {
  return {
    id: `${ORDER_UNSCHEDULED_TOAST_KEY}`,
    type: NotificationType.SUCCESS,
    message: i18n.t('notifications:unscheduleVisit.unschedule_success', { count }),
    priority: true,
    dataCy: 'order-unscheduled-success-notification',
    linkMessage: onButtonClick ? 
      i18n.t('notifications:unscheduleVisit.unschedule_success_date_link', { count, date: DateTime.fromISO(date).toFormat(defaultDisplayDateFormat) }) : undefined,
    linkOnClick: onButtonClick,
  };
};
